import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ApiKeys, SetupConfiguration, SetupStep } from '@/types/setup';
import { validateApiKey } from '@/api-frontend/validateApiKey';
import { requestGeolocation } from '@/api-frontend/requestGeolocation';
import { saveSetupConfig } from '@/api-frontend/saveSetupConfig';

interface SetupState {
  config: SetupConfiguration;
  currentStep: SetupStep;
  loading: boolean;
  error: string | null;
  validationStatus: {
    openWeatherMapKey: boolean;
    newsApiKey: boolean;
  };
}

// Load setup configuration from localStorage
const loadSetupFromStorage = (): SetupConfiguration => {
  try {
    const stored = localStorage.getItem('setup-config');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to load setup configuration from localStorage:', error);
  }
  
  return {
    apiKeys: {
      openWeatherMapKey: '',
      newsApiKey: '',
    },
    locationPermissionGranted: false,
    currentLocation: null,
    setupCompleted: false,
  };
};

// Initialize based on stored configuration
const storedConfig = loadSetupFromStorage();
const getInitialStep = (config: SetupConfiguration): SetupStep => {
  if (!config.apiKeys.openWeatherMapKey || !config.apiKeys.newsApiKey) {
    return 'apiKeys';
  }
  if (!config.locationPermissionGranted) {
    return 'location';
  }
  return 'completed';
};

const initialState: SetupState = {
  config: storedConfig,
  currentStep: getInitialStep(storedConfig),
  loading: false,
  error: null,
  validationStatus: {
    openWeatherMapKey: !!storedConfig.apiKeys.openWeatherMapKey,
    newsApiKey: !!storedConfig.apiKeys.newsApiKey,
  },
};

// Async thunks
export const validateApiKeys = createAsyncThunk(
  'setup/validateApiKeys',
  async (apiKeys: ApiKeys) => {
    const [weatherResult, newsResult] = await Promise.all([
      validateApiKey('openweathermap', apiKeys.openWeatherMapKey),
      validateApiKey('newsapi', apiKeys.newsApiKey),
    ]);

    return {
      openWeatherMapKey: weatherResult.isValid,
      newsApiKey: newsResult.isValid,
      apiKeys,
    };
  }
);

export const requestLocationPermission = createAsyncThunk(
  'setup/requestLocationPermission',
  async () => {
    const result = await requestGeolocation();
    return result;
  }
);

export const saveConfiguration = createAsyncThunk(
  'setup/saveConfiguration',
  async (config: SetupConfiguration) => {
    const result = await saveSetupConfig(config);
    return result;
  }
);

const setupSlice = createSlice({
  name: 'setup',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<SetupStep>) => {
      state.currentStep = action.payload;
    },
    updateApiKeys: (state, action: PayloadAction<Partial<ApiKeys>>) => {
      state.config.apiKeys = { ...state.config.apiKeys, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    resetSetup: (state) => {
      state.config = {
        apiKeys: {
          openWeatherMapKey: '',
          newsApiKey: '',
        },
        locationPermissionGranted: false,
        currentLocation: null,
        setupCompleted: false,
      };
      state.currentStep = 'apiKeys';
      state.validationStatus = {
        openWeatherMapKey: false,
        newsApiKey: false,
      };
      localStorage.removeItem('setup-config');
    },
  },
  extraReducers: (builder) => {
    builder
      // Validate API Keys
      .addCase(validateApiKeys.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateApiKeys.fulfilled, (state, action) => {
        state.loading = false;
        state.validationStatus = {
          openWeatherMapKey: action.payload.openWeatherMapKey,
          newsApiKey: action.payload.newsApiKey,
        };
        state.config.apiKeys = action.payload.apiKeys;
        
        // Auto-advance to location step if both keys are valid
        if (action.payload.openWeatherMapKey && action.payload.newsApiKey) {
          state.currentStep = 'location';
        }
      })
      .addCase(validateApiKeys.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to validate API keys';
      })
      
      // Request Location Permission
      .addCase(requestLocationPermission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestLocationPermission.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success && action.payload.position) {
          state.config.locationPermissionGranted = true;
          state.config.currentLocation = action.payload.position;
          state.currentStep = 'completed';
        } else {
          state.error = action.payload.error || 'Failed to get location permission';
        }
      })
      .addCase(requestLocationPermission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to request location permission';
      })
      
      // Save Configuration
      .addCase(saveConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        state.config = action.payload;
        state.config.setupCompleted = true;
        localStorage.setItem('setup-config', JSON.stringify(state.config));
      })
      .addCase(saveConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to save configuration';
      });
  },
});

export const { setCurrentStep, updateApiKeys, clearError, resetSetup } = setupSlice.actions;
export default setupSlice.reducer;